// src/pages/Login.js — Admin portal restricted to owner/team_leader roles
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from '../utils/api';

// Merged portal: ALL roles can log in now (was owner/team_leader only,
// back when this was admin-portal's standalone Login.js). Warehouse staff
// (stock_removal/checking/packing/delivery_person) need to log in too,
// since Vouchers/Assign Deliveries/Stage Override/Warehouse tabs are now
// part of this same single portal.
const ALLOWED_ROLES = ['owner', 'team_leader', 'stock_removal', 'checking', 'booking', 'billing', 'delivery_person'];

function normalizePhone(raw) {
  let p = raw.trim().replace(/\s+/g, '');
  if (p.startsWith('+91')) p = p.slice(3);
  else if (p.startsWith('0091')) p = p.slice(4);
  else if (p.startsWith('91') && p.length === 12) p = p.slice(2);
  return p;
}

export default function Login() {
  const [phone, setPhone]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Forced password change flow — shown instead of navigating away when
  // an admin has reset this account's password (see Team Management).
  // The token from the login response is stored right away so the
  // change-password call below can authenticate, but dms_user is only
  // stored once the change actually succeeds — nothing about being
  // mid-flow here grants any other page access.
  const [forcedChange, setForcedChange] = useState(false);
  const [pendingUser,  setPendingUser]  = useState(null);
  const [newPw1,       setNewPw1]       = useState('');
  const [newPw2,       setNewPw2]       = useState('');
  const [changingPw,   setChangingPw]   = useState(false);
  const [changeError,  setChangeError]  = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await post('/api/auth/login', { phone, password });

      if (!ALLOWED_ROLES.includes(data.user?.role)) {
        setError('Access denied — the admin portal is for owners and team leaders only.');
        return;
      }

      if (data.user?.forcePasswordChange) {
        localStorage.setItem('dms_token', data.accessToken);
        setPendingUser(data.user);
        setForcedChange(true);
        return;
      }

      localStorage.setItem('dms_token', data.accessToken);
      localStorage.setItem('dms_user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid phone or password');
    } finally {
      setLoading(false);
    }
  }

  async function handleForcedChange(e) {
    e.preventDefault();
    setChangeError('');
    if (newPw1 !== newPw2) {
      setChangeError("New passwords don't match");
      return;
    }
    if (newPw1.length < 6) {
      setChangeError('New password must be at least 6 characters');
      return;
    }
    setChangingPw(true);
    try {
      const result = await post('/api/auth/change-password', {
        currentPassword: password,
        newPassword: newPw1,
      });
      localStorage.setItem('dms_token', result.accessToken);
      localStorage.setItem('dms_user', JSON.stringify({ ...pendingUser, forcePasswordChange: false }));
      navigate('/dashboard');
    } catch (err) {
      setChangeError(err.message || 'Could not change password');
    } finally {
      setChangingPw(false);
    }
  }

  if (forcedChange) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-700 to-brand-500 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔑</span>
            </div>
            <h1 className="text-2xl font-bold text-ink dark:text-gray-100">Set a New Password</h1>
            <p className="text-sm text-ink-muted dark:text-gray-400 mt-1">
              Your password was reset by an administrator — choose a new one to continue, {pendingUser?.name}.
            </p>
          </div>

          <form onSubmit={handleForcedChange}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
              <input
                type="password"
                value={newPw1}
                onChange={e => setNewPw1(e.target.value)}
                placeholder="At least 6 characters"
                required
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
              <input
                type="password"
                value={newPw2}
                onChange={e => setNewPw2(e.target.value)}
                placeholder="Type it again"
                required
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
              />
            </div>

            {changeError && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-300 rounded-xl text-sm text-red-700">
                {changeError}
              </div>
            )}

            <button
              type="submit"
              disabled={changingPw}
              className="w-full py-3 bg-brand-700 text-white rounded-xl font-semibold text-sm hover:bg-brand-500 transition-colors disabled:opacity-60"
            >
              {changingPw ? 'Setting password...' : 'Set New Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-700 to-brand-500 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/medji-icon.png" alt="Medji" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
          <h1 className="text-2xl font-bold text-ink dark:text-gray-100">Medji</h1>
          <p className="text-sm text-ink-muted dark:text-gray-400 mt-1">Delivery Management System</p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
            <input
              type="text"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="Enter login mobile number"
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-300 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-brand-700 text-white rounded-xl font-semibold text-sm hover:bg-brand-500 transition-colors disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
          Medji · Delivery Management System
        </p>
      </div>
    </div>
  );
}
